import React, { useEffect } from "react";

import { cn } from "../lib/utils";

/**
 * Slide-in left drawer for mobile navigation. Rendered alongside (and
 * mutually exclusive with) the desktop sidebar — Layout shows whichever
 * fits the current viewport via `lg:hidden` + `hidden lg:flex`.
 *
 * Hand-rolled (no headless-ui dep) — ~30 lines. Closes on:
 *   - backdrop tap
 *   - Escape key
 *   - explicit `onClose` from caller (e.g., route change in Layout)
 *
 * Animation is CSS-only via Tailwind transition + transform classes.
 * Body scroll-lock isn't applied — the drawer is short enough that
 * background-scroll-bleed isn't a real concern at our viewport sizes,
 * and users expect to be able to close-by-tapping-outside which would
 * conflict with scroll-locking.
 */
interface DrawerProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** Width class — defaults to `w-64`. Caller can override for wider
   *  drawers. The drawer is always a left-side panel. */
  widthClass?: string;
}

export const Drawer: React.FC<DrawerProps> = ({
  open,
  onClose,
  children,
  widthClass = "w-64",
}) => {
  // Close on Escape so keyboard users aren't stuck.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <>
      {/* Backdrop. z-[1090] keeps us above Leaflet's panes + controls
          (max z-index ~1000) so map pages don't punch through the
          drawer. */}
      <div
        onClick={onClose}
        aria-hidden="true"
        className={cn(
          "fixed inset-0 bg-black/40 z-[1090] transition-opacity lg:hidden",
          open ? "opacity-100" : "opacity-0 pointer-events-none",
        )}
      />
      {/* Panel — z-[1100] sits above the backdrop (z-[1090]). */}
      <aside
        role="dialog"
        aria-modal="true"
        className={cn(
          "fixed left-0 top-0 h-full bg-primary-500 text-white z-[1100] shadow-xl",
          "transform transition-transform duration-200 ease-out lg:hidden",
          widthClass,
          open ? "translate-x-0" : "-translate-x-full",
        )}
      >
        {children}
      </aside>
    </>
  );
};

export default Drawer;
